import { Image } from "antd";
import { PLACEHOLDER_IN_BASE64 } from "constants/common";
import { WIDGET_GALLERY_POSITION, WIDGET_GALLERY_TYPE } from "constants/widgetBuilder";
import { useMemo } from "react";
import {
	StyledMediaBox,
	StyledWidgetGalleryWrapper,
} from "styled-components/WidgetBuilder/WidgetGallery";
import WidgetAnimatedGallery from "./WidgetAnimatedGallery";
import { StyledCol, StyledRow, StyledWrapper } from "styled-components/Common/CommonStyled";

const splitIntoRows = (images) => {
	const maxItemsPerRow = images.length === 3 || images.length >= 5 ? 3 : 2;
	const rows = [];

	for (let i = 0; i < images.length; i += maxItemsPerRow) {
		rows.push(images.slice(i, i + maxItemsPerRow));
	}

	return rows;
};

const ImageGrid = ({ images, isComplexGrid }) => {
	const rows = splitIntoRows(images);
	const imageProps = {
		preview: false,
		width: "100%",
		height: "100%",
		fallback: PLACEHOLDER_IN_BASE64.IMAGE,
	};

	return isComplexGrid ? (
		<StyledWidgetGalleryWrapper>
			{rows.map((row, rowIndex) => {
				const colSpan = Math.floor(24 / row.length);

				return (
					<StyledRow
						gutter={[2, 2]}
						style={{ marginBottom: rowIndex !== rows.length - 1 ? 2 : 0 }}
						key={rowIndex}>
						{row.map((media, i) => (
							<StyledCol key={i} span={colSpan}>
								<StyledMediaBox>
									<Image
										className="absolute"
										src={media.thumbnail}
										alt={`Widget Gallery Thumbnail ${i + 1}`}
										{...imageProps}
									/>
								</StyledMediaBox>
							</StyledCol>
						))}
					</StyledRow>
				);
			})}
		</StyledWidgetGalleryWrapper>
	) : (
		<StyledWidgetGalleryWrapper className="grid" columns={images.length}>
			{images.map((media, index) => (
				<StyledMediaBox key={`general-${index}`} aspectRatio="1">
					<Image
						src={media.thumbnail}
						alt={`Widget Gallery Thumbnail ${index + 1}`}
						{...imageProps}
					/>
				</StyledMediaBox>
			))}
		</StyledWidgetGalleryWrapper>
	);
};

const WidgetGallery = (props) => {
	const { isMoreField = false, detailWidget, isMobile = false } = props;
	const {
		metadata: {
			gallery: { width = 100, position, mediaList = [], type = WIDGET_GALLERY_TYPE.STATIC } = {},
		} = {},
	} = detailWidget || {};

	const widgetGalleryType = useMemo(() => {
		return {
			isStatic: type === WIDGET_GALLERY_TYPE.STATIC,
			isAnimated: type === WIDGET_GALLERY_TYPE.ANIMATED,
		};
	}, [type]);

	const galleryHeight = useMemo(() => {
		if (widgetGalleryType.isAnimated) {
			if (isMobile || (isMoreField && position === WIDGET_GALLERY_POSITION.BELOW)) {
				return "180px";
			}

			return "200px";
		}

		return "auto";
	}, [type, position, isMobile, isMoreField]);

	const renderGallery = () => {
		const MAX_ITEMS = 6;
		return !widgetGalleryType.isAnimated ? (
			<ImageGrid
				images={mediaList.length > MAX_ITEMS ? mediaList.slice(0, MAX_ITEMS) : mediaList}
				isComplexGrid={isMobile || (!!isMoreField && position === WIDGET_GALLERY_POSITION.BELOW)}
			/>
		) : (
			<WidgetAnimatedGallery animatedMediaList={mediaList} isMobile={isMobile} />
		);
	};

	return mediaList.length > 0 ? (
		<StyledWrapper
			width={`${width}%`}
			height={galleryHeight}
			margin={
				isMoreField && position === WIDGET_GALLERY_POSITION.BELOW
					? "0 auto 24px auto"
					: "24px auto 0 auto"
			}>
			{renderGallery()}
		</StyledWrapper>
	) : (
		<></>
	);
};

export default WidgetGallery;
